import { useDeferredValue, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { CompactAlert } from '~/components/common/CompactAlert'
import { DeleteConfirmDialog } from '~/components/common/DeleteConfirmDialog'
import {
  type PlantMasterRow,
  convertListPlantMastersResponse,
  useListPlantMasters,
} from '~/hooks/useListPlantMasters'
import { MasterCrudTabShell } from '~/pages/AdminOcctoMaster/shell/MasterCrudTabShell'
import { useMasterCrudTab } from '~/pages/AdminOcctoMaster/shell/useMasterCrudTab'
import { useMasterInlineDelete } from '~/pages/AdminOcctoMaster/shell/useMasterInlineDelete'
import { CreatePlantMasterModal } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/components/CreatePlantMasterModal'
import { EditPlantMasterModal } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/components/EditPlantMasterModal'
import { PlantMasterTable } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/components/PlantMasterTable'
import { useDeletePlantMaster } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/hooks/useDeletePlantMaster'
import { plantMasterCsvColumns } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/labels'
import { ExpiredFilterToggle } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredFilterToggle'
import { MasterFilterToolbar } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'

const DEFAULT_AREA: AreaCode = AreaCode.UNSPECIFIED

function filterRows(
  rows: PlantMasterRow[],
  areaCode: AreaCode,
  searchText: string,
): PlantMasterRow[] {
  const trimmed = searchText.trim().toLowerCase()
  return rows
    .filter((row) => {
      if (areaCode !== AreaCode.UNSPECIFIED && row.areaCode !== areaCode) {
        return false
      }
      if (trimmed === '') {
        return true
      }
      return (
        row.plantCode.toLowerCase().includes(trimmed) ||
        row.plantName.toLowerCase().includes(trimmed) ||
        row.plantNameAbbr.toLowerCase().includes(trimmed)
      )
    })
    .slice()
    .sort((a, b) => a.plantCode.localeCompare(b.plantCode))
}

export const PlantMasterTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useListPlantMasters()

  const [selectedAreaCode, setSelectedAreaCode] =
    useState<AreaCode>(DEFAULT_AREA)
  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)

  const rows = useMemo(
    () => (data !== undefined ? convertListPlantMastersResponse(data) : []),
    [data],
  )
  const filteredRows = useMemo(
    () => filterRows(rows, selectedAreaCode, deferredSearchText),
    [rows, selectedAreaCode, deferredSearchText],
  )

  // エリア不一致 (系統コードのエリアと、それを使うユニットの発電所のエリアが違う): 一覧上部の注意の件数 (判定は backend の応答値)
  const mismatchedPlantCount = useMemo(
    () => rows.filter((row) => row.areaMismatchedUnits.length > 0).length,
    [rows],
  )

  // 注: design では 電源ユニットは「失効」ではなく「廃止」トグル
  // (spec plant-unit.md AC-PU07) だが本実装は未対応。 暫定的に共通の失効トグルを
  // 流用しつつ subject だけ「電源ユニット」に差し替える (廃止フィルタ化は別 issue)。
  const subject = t('page:adminOcctoMaster.common.subject.plant')
  const tab = useMasterCrudTab<PlantMasterRow>({
    data,
    isLoading,
    isError,
    error,
    errorKeyPrefix: 'page:adminOcctoMaster.plantMaster.serverError',
    filteredRows,
    csv: { columns: plantMasterCsvColumns(), fileBase: 'plant' },
    expiry: {
      getPeriod: (row) => ({
        validFrom: row.validFrom,
        validTo: row.validTo,
      }),
      subject,
    },
  })
  const { handleDeleteConfirm, isDeleting } =
    useMasterInlineDelete<PlantMasterRow>({
      modalState: tab.modalState,
      closeModal: tab.closeModal,
      setDeleteError: tab.setDeleteError,
      errorKeyPrefix: 'page:adminOcctoMaster.plantMaster.serverError',
      useDelete: useDeletePlantMaster,
    })

  const deleteRow = tab.modalState.kind === 'delete' ? tab.modalState.row : null

  return (
    <MasterCrudTabShell
      createModal={
        // 条件 mount (閉じたら form ごと破棄): 常時 mount + isOpen 切替だと
        // 保存成功時の reset() 直後に mount 中の field array 行入力 (所属事業者
        // リスト、defaultValues に初期 1 行) が RHF の再 register で直前の値を
        // 書き戻し、次回 open で行が残留する (#6072、詳細は
        // DemandProcurementPlanMaster/index.tsx の同コメント)。
        tab.modalState.kind === 'create' ? (
          <CreatePlantMasterModal isOpen onClose={tab.closeModal} />
        ) : null
      }
      csvExportDisabledReason={tab.csvExportDisabledReason}
      deleteDialog={
        deleteRow !== null ? (
          <DeleteConfirmDialog
            entityName={t(
              'page:adminOcctoMaster.plantMaster.deleteEntityName',
              {
                code: deleteRow.plantCode,
                name: deleteRow.plantName,
              },
            )}
            errorIsRetriable={tab.deleteError?.retriable}
            errorMessage={tab.deleteError?.message}
            isLoading={isDeleting}
            isOpen={tab.modalState.kind === 'delete'}
            onCancel={tab.closeModal}
            onConfirm={() => {
              void handleDeleteConfirm()
            }}
            title={t('page:adminOcctoMaster.plantMaster.delete.confirmTitle')}
          />
        ) : null
      }
      editModal={
        tab.modalState.kind === 'edit' ? (
          <EditPlantMasterModal
            row={tab.modalState.row}
            isOpen
            onClose={tab.closeModal}
          />
        ) : null
      }
      filterToolbar={
        <>
          {mismatchedPlantCount > 0 && (
            <CompactAlert
              description={t(
                'page:adminOcctoMaster.plantMaster.areaMismatchDescription',
              )}
              status="warning"
              title={t('page:adminOcctoMaster.plantMaster.areaMismatchTitle', {
                count: mismatchedPlantCount,
              })}
            />
          )}
          <MasterFilterToolbar
            onSearchTextChange={setSearchText}
            onSelectArea={setSelectedAreaCode}
            searchPlaceholder={t(
              'page:adminOcctoMaster.plantMaster.toolbar.searchPlaceholder',
            )}
            searchText={searchText}
            selectedAreaCode={selectedAreaCode}
            trailing={
              <ExpiredFilterToggle
                expiredCount={tab.expiredCount}
                includeExpired={tab.includeExpired}
                onChange={tab.setIncludeExpired}
                subject={subject}
              />
            }
          />
        </>
      }
      onAddIndividual={tab.openCreate}
      onCsvExport={tab.csvExportEnabled ? tab.handleCsvExport : undefined}
      showInitialLoading={tab.showInitialLoading}
      table={
        <PlantMasterTable
          emptyLabel={tab.emptyLabel}
          errorLabel={tab.errorLabel}
          isLoading={isLoading}
          onDelete={tab.openDelete}
          onEdit={tab.openEdit}
          rows={tab.visibleRows}
          today={tab.today}
        />
      }
    />
  )
}
